import { Dispatch, SetStateAction, SyntheticEvent } from "react";

import { useTranslation } from "react-i18next";

import { isEqual } from "lodash";
import BannerMessage, {
  BANNER_MESSAGE_STATUSES
} from "@/components/BannerMessage/BannerMessage";
import Grid from "@mui/material/Grid";

import { ApiResources } from "../../../../../api/resources";
import { useIntegrationsOptions } from "../../../../../Pages/Integrations/hooks/useIntegrationsOptions";
import {
  INTEGRATION_FIELD_NAMES,
  IntegrationErrors,
  IntegrationObjectState
} from "../../../../../Pages/Integrations/types";
import {
  ID_TYPE,
  inputFieldMarginBottom,
  useDateTime
} from "../../../../../shared";
import Autocomplete from "../../../../Autocomplete/Autocomplete";
import Checkbox from "../../../../CheckBox/Checkbox";
import InputField from "../../../../InputField/InputField";
import MaskedPasswordInput from "../../../../InputField/MaskedPasswordInput";
import PaperBox from "../../../../PaperBox/PaperBox";
import QueryDropdown from "../../../../QueryDropdown/QueryDropdown";
import useIntegrationsInputs from "./hooks/useIntegrationsInputs";

interface IntegrationDialogContentProps {
  isLoading: boolean;
  integrationsObject: IntegrationObjectState;
  errors: IntegrationErrors;
  hasUpdatedEnable: boolean;
  setErrors: Dispatch<SetStateAction<IntegrationErrors>>;
  setIntegrationsObject: Dispatch<SetStateAction<IntegrationObjectState>>;
  setHasUpdatedEnable: Dispatch<SetStateAction<boolean>>;
  readOnlyFields?: INTEGRATION_FIELD_NAMES[];
  optionalPassword?: boolean;
  hasInteracted?: boolean;
  setHasInteracted?: Dispatch<SetStateAction<boolean>>;
}

const IntegrationDialogContent = ({
  isLoading,
  integrationsObject,
  errors,
  hasUpdatedEnable,
  setErrors,
  setIntegrationsObject,
  setHasUpdatedEnable,
  readOnlyFields,
  optionalPassword,
  hasInteracted,
  setHasInteracted
}: IntegrationDialogContentProps) => {
  const { t } = useTranslation();

  const { driverFileDestinationOptions, vehicleFolderNamingOptions } =
    useIntegrationsOptions();

  const { convertToCurrentDateFormat } = useDateTime();

  const { handleBlur, clearFieldError } = useIntegrationsInputs(
    errors,
    optionalPassword,
    setErrors
  );

  const handleChange = (field: INTEGRATION_FIELD_NAMES, value: any) => {
    setIntegrationsObject(prev => {
      const getFieldValue = () => {
        if (field === INTEGRATION_FIELD_NAMES.IncludeSubCompanies) {
          return !prev.includeSubCompanies;
        }

        if (field === INTEGRATION_FIELD_NAMES.SyncHistoricalFiles) {
          return !prev.syncHistoricalFiles;
        }

        if (field === INTEGRATION_FIELD_NAMES.Enabled) {
          !hasUpdatedEnable && setHasUpdatedEnable(true);

          return !prev.enabled;
        }

        return value;
      };

      return {
        ...prev,
        [field]: getFieldValue()
      };
    });

    const fieldAsErrorType = field as keyof IntegrationErrors;

    if (errors[fieldAsErrorType]) {
      clearFieldError(fieldAsErrorType);
    }

    if (setHasInteracted) {
      !hasInteracted && setHasInteracted(true);
    }
  };

  const driverFilesRawDate = "2025-12-03";
  const formattedDate = convertToCurrentDateFormat(driverFilesRawDate);

  const showInfoBanner =
    Boolean(integrationsObject.syncHistoricalFiles) &&
    isEqual(
      integrationsObject[INTEGRATION_FIELD_NAMES.DriverFolderNaming],
      driverFileDestinationOptions[0]
    );

  return (
    <Grid
      container
      size={12}
      sx={{
        justifyContent: "flex-end"
      }}
      data-testid="integration-content-container-grid"
      spacing={3}
    >
      <Grid size={{ xs: 12, md: 6 }} sx={{ paddingTop: 0 }}>
        <PaperBox
          paperTitle={t("Dialog##sftp server settings")}
          testId="integrations-server-settings-card"
          customStyling={{ padding: "24px 24px 8px 24px" }}
        >
          <InputField
            data-testid="integrations-host-field"
            fullWidth
            required
            value={integrationsObject[INTEGRATION_FIELD_NAMES.Host]}
            labelLeft={t("Dialog##host")}
            placeholder={t("Dialog##sftp server hostname or ip")}
            size="medium"
            onBlur={e =>
              handleBlur(INTEGRATION_FIELD_NAMES.Host, e.target.value)
            }
            onChange={e =>
              handleChange(INTEGRATION_FIELD_NAMES.Host, e.target.value)
            }
            errorText={errors[INTEGRATION_FIELD_NAMES.Host]}
            disabled={isLoading}
            customStyle={inputFieldMarginBottom}
          />
          <InputField
            data-testid="integrations-port-field"
            value={integrationsObject[INTEGRATION_FIELD_NAMES.Port]}
            fullWidth
            required
            labelLeft={t("Dialog##port")}
            placeholder={t("Dialog##sftp server port number")}
            size="medium"
            onBlur={e =>
              handleBlur(INTEGRATION_FIELD_NAMES.Port, e.target.value)
            }
            onChange={e =>
              handleChange(INTEGRATION_FIELD_NAMES.Port, e.target.value)
            }
            errorText={errors[INTEGRATION_FIELD_NAMES.Port]}
            disabled={isLoading}
            customStyle={inputFieldMarginBottom}
          />
          <InputField
            data-testid="integrations-remote-directory-field"
            value={integrationsObject[INTEGRATION_FIELD_NAMES.RemotePath]}
            fullWidth
            labelLeft={t("Dialog##remote directory path optional")}
            placeholder={t("Dialog##upload folder blank for root")}
            size="medium"
            onChange={e =>
              handleChange(INTEGRATION_FIELD_NAMES.RemotePath, e.target.value)
            }
            onBlur={e =>
              handleBlur(INTEGRATION_FIELD_NAMES.RemotePath, e.target.value)
            }
            errorText={errors[INTEGRATION_FIELD_NAMES.RemotePath]}
            disabled={isLoading}
            customStyle={inputFieldMarginBottom}
          />
          <InputField
            data-testid="integrations-username-field"
            fullWidth
            required
            value={integrationsObject[INTEGRATION_FIELD_NAMES.Username]}
            labelLeft={t("Dialog##username")}
            placeholder={t("Dialog##sftp username")}
            size="medium"
            onChange={e =>
              handleChange(INTEGRATION_FIELD_NAMES.Username, e.target.value)
            }
            onBlur={e =>
              handleBlur(INTEGRATION_FIELD_NAMES.Username, e.target.value)
            }
            errorText={errors[INTEGRATION_FIELD_NAMES.Username]}
            disabled={isLoading}
            customStyle={inputFieldMarginBottom}
          />
          <MaskedPasswordInput
            data-testid="integrations-password-field"
            fullWidth
            required={!optionalPassword}
            value={integrationsObject[INTEGRATION_FIELD_NAMES.Password]}
            name={INTEGRATION_FIELD_NAMES.Password}
            labelLeft={t("Dialog##password")}
            placeholder={t("Dialog##sftp password")}
            onChange={handleChange}
            handleBlur={handleBlur}
            errorText={errors[INTEGRATION_FIELD_NAMES.Password]}
            disabled={isLoading}
            handleChange={handleChange}
            customStyle={inputFieldMarginBottom}
          />
        </PaperBox>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }} sx={{ paddingTop: 0 }}>
        <PaperBox
          paperTitle={t("Settings##general settings")}
          testId="integrations-general-settings-card"
          customStyling={{ padding: "24px 24px 16px 24px" }}
        >
          <QueryDropdown
            testId="integrations-company-dropdown"
            disableClearable
            resource={ApiResources.CompanyList}
            idType={ID_TYPE.Id}
            value={integrationsObject[INTEGRATION_FIELD_NAMES.SelectedCompany]}
            size="medium"
            fullWidth
            isRequired
            change={(_, val) =>
              handleChange(INTEGRATION_FIELD_NAMES.SelectedCompany, val)
            }
            readOnly={readOnlyFields?.includes(
              INTEGRATION_FIELD_NAMES.SelectedCompany
            )}
            labelLeft={t("Dialog##company")}
            textFieldParams={{ placeholder: t("Dialog##select") }}
            customStyle={inputFieldMarginBottom}
            isDisabled={isLoading}
          />
          <InputField
            data-testid="integrations-name-field"
            size="medium"
            fullWidth
            required
            value={integrationsObject[INTEGRATION_FIELD_NAMES.Name]}
            labelLeft={t("Dialog##sftp integration##sftp name")}
            placeholder={t("Dialog##introduce name")}
            onBlur={e =>
              handleBlur(INTEGRATION_FIELD_NAMES.Name, e.target.value)
            }
            onChange={e =>
              handleChange(INTEGRATION_FIELD_NAMES.Name, e.target.value)
            }
            errorText={errors[INTEGRATION_FIELD_NAMES.Name]}
            disabled={isLoading}
            customStyle={inputFieldMarginBottom}
          />
          <QueryDropdown
            testId="integrations-file-format-dropdown"
            useValueForEquality
            disableClearable
            fullWidth
            isRequired
            resource={ApiResources.UsersSettingsFileFormats}
            idType={ID_TYPE.Id}
            size="medium"
            isDisabled={isLoading}
            value={integrationsObject[INTEGRATION_FIELD_NAMES.FileFormat]}
            change={(_, val) =>
              handleChange(INTEGRATION_FIELD_NAMES.FileFormat, val)
            }
            labelLeft={t("Dialog##file format")}
            customStyle={inputFieldMarginBottom}
            textFieldParams={{
              placeholder: t("Dialog##select file format example")
            }}
          />
          <Autocomplete
            size="medium"
            labelLeft={t("Dialog##vehicles folder naming")}
            data-testid="integrations-vehicle-folder-naming-dropdown"
            isRequired
            fullWidth
            disableClearable
            value={
              integrationsObject[INTEGRATION_FIELD_NAMES.VehicleFolderNaming]
            }
            options={vehicleFolderNamingOptions}
            getOptionLabel={option => option?.name}
            isOptionEqualToValue={(option, value) =>
              option?.value === value?.value
            }
            onChange={(_: SyntheticEvent, value: any) =>
              handleChange(INTEGRATION_FIELD_NAMES.VehicleFolderNaming, value)
            }
            disabled={isLoading}
            customStyle={inputFieldMarginBottom}
            textFieldParams={{
              placeholder: t("Dialog##select format")
            }}
          />
          <Autocomplete
            size="medium"
            labelLeft={t("Dialog##driver file destination")}
            data-testid="integrations-driver-file-destination-dropdown"
            isRequired
            fullWidth
            disableClearable
            labelIcon={t("Dialog##driver file destination tooltip")}
            value={
              integrationsObject[INTEGRATION_FIELD_NAMES.DriverFolderNaming]
            }
            options={driverFileDestinationOptions}
            getOptionLabel={option => option?.name}
            isOptionEqualToValue={(option, value) =>
              option?.value === value?.value
            }
            onChange={(_: SyntheticEvent, value: any) =>
              handleChange(INTEGRATION_FIELD_NAMES.DriverFolderNaming, value)
            }
            disabled={isLoading}
            customStyle={inputFieldMarginBottom}
            textFieldParams={{
              placeholder: t(
                "Dialog##integrations##select driver files destination"
              )
            }}
          />
          <div style={{ display: "flex", flexDirection: "column" }}>
            {showInfoBanner && (
              <BannerMessage
                data-testid="driver-files-destination-info-banner"
                status={BANNER_MESSAGE_STATUSES.Info}
                title={t("Dialog##integrations##driver files info banner", {
                  formattedDate
                })}
                externalCustomStyle={{ marginBottom: "16px" }}
              />
            )}
            <Checkbox
              id="integration-include-sub-companies-checkbox"
              infoIconTooltip={t("Dialog##integrations##include files tooltip")}
              infoIconTooltipSize="medium"
              checked={integrationsObject.includeSubCompanies}
              label={t(
                "Dialog##integrations##include files from all sub companies"
              )}
              onChange={e =>
                handleChange(
                  INTEGRATION_FIELD_NAMES.IncludeSubCompanies,
                  e.target.value
                )
              }
              disabled={isLoading}
              customStyle={{
                ...inputFieldMarginBottom,
                "@media (min-width: 840px) and (max-width: 875px)": {
                  marginRight: "28px"
                }
              }}
            />
            <Checkbox
              id="integration-include-historical-files-checkbox"
              infoIconTooltip={t(
                "Dialog##integrations##include historical files tooltip"
              )}
              infoIconTooltipSize="medium"
              checked={integrationsObject.syncHistoricalFiles}
              label={t("Dialog##integrations##include historical files")}
              onChange={e =>
                handleChange(
                  INTEGRATION_FIELD_NAMES.SyncHistoricalFiles,
                  e.target.value
                )
              }
              disabled={isLoading}
              customStyle={inputFieldMarginBottom}
            />

            <Checkbox
              id="sftp-disable-integration-checkbox"
              infoIconTooltip={t(
                "Dialog##integrations##deactivate integration tooltip"
              )}
              infoIconTooltipSize="medium"
              checked={!integrationsObject.enabled}
              label={t("Dialog##integrations##deactivate sftp")}
              onChange={e =>
                handleChange(INTEGRATION_FIELD_NAMES.Enabled, e.target.value)
              }
              disabled={isLoading}
              customStyle={{
                ...inputFieldMarginBottom,

                "@media (min-width: 840px) and (max-width: 875px)": {
                  marginRight: "28px"
                }
              }}
            />
          </div>
        </PaperBox>
      </Grid>
    </Grid>
  );
};

export default IntegrationDialogContent;
